import { Head, Link } from '@inertiajs/react';

import {
    Btn,
    DesignFooter,
    DesignHeader,
    I,
    TrustBadge,
    UtilityBar,
} from '@/Components/Design';

type Props = {
    faqs: Array<{ question: string; answer: string }>;
};

export default function ForAttorneys({ faqs }: Props) {
    return (
        <>
            <Head>
                <title>For attorneys — injury.help</title>
            </Head>
            <div style={{ background: 'var(--paper)', minHeight: '100vh' }}>
                <UtilityBar />
                <DesignHeader active="For Attorneys" />

                {/* Hero */}
                <section data-fa-section="hero" style={{ maxWidth: 1440, margin: '0 auto', padding: '64px 56px 48px' }}>
                    <nav aria-label="Breadcrumb">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--ink-mute)', marginBottom: 24 }}>
                        <Link href="/" style={{ color: 'inherit', textDecoration: 'none' }}>Home</Link>
                        <span>/</span>
                        <span aria-current="page" style={{ color: 'var(--ink)' }}>For attorneys</span>
                    </div></nav>
                    <div data-fa-hero-grid style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: 64, alignItems: 'flex-start' }}>
                        <div>
                            <div style={{ fontSize: 12, letterSpacing: '.12em', textTransform: 'uppercase', color: 'var(--ember)', marginBottom: 10, fontWeight: 600 }}>For attorneys</div>
                            <h1 style={{ fontSize: 56, lineHeight: 1.05, letterSpacing: '-0.025em', marginBottom: 20, fontFamily: 'var(--font-serif)', fontWeight: 500 }}>
                                A directory built for <em>vetted</em> plaintiff-side counsel.
                            </h1>
                            <p style={{ fontSize: 18, color: 'var(--ink-soft)', lineHeight: 1.55, marginBottom: 28, maxWidth: 580 }}>
                                Free listing for every bar-verified attorney. Your information goes straight to the prospective client — no call center, no inbound fees per click.
                            </p>
                            <div data-fa-cta-row style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                                <Btn kind="primary" size="lg" icon={I.arrow} href="/auth/signup">Claim your profile</Btn>
                                <Btn kind="secondary" size="lg" href="/auth/login">Sign in</Btn>
                            </div>
                            <div data-trust-row style={{ marginTop: 28, display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 480 }}>
                                <TrustBadge tone="sage" icon={I.check} label="Free listing — no per-click fees" />
                                <TrustBadge tone="sage" icon={I.check} label="Bar-verified directory only" />
                                <TrustBadge tone="sage" icon={I.check} label="Direct lead delivery (no call center)" />
                                <TrustBadge tone="sage" icon={I.check} label="Edit + claim your own bio and contact" />
                            </div>
                        </div>
                        <aside data-card data-fa-aside style={{ background: 'var(--card)', border: '1px solid var(--rule)', borderRadius: 16, padding: 28, boxShadow: 'var(--shadow-card)' }}>
                            <div style={{ fontSize: 11, letterSpacing: '.12em', textTransform: 'uppercase', color: 'var(--ember)', marginBottom: 8, fontWeight: 600 }}>How it works</div>
                            <ol style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 18 }}>
                                <li style={{ display: 'grid', gridTemplateColumns: '32px 1fr', gap: 14, alignItems: 'flex-start' }}>
                                    <div className="pip-mono" style={{ fontSize: 13, color: 'var(--ember)', fontWeight: 600 }}>01</div>
                                    <div>
                                        <div style={{ fontFamily: 'var(--font-serif)', fontSize: 17, fontWeight: 500 }}>Claim your profile</div>
                                        <div style={{ fontSize: 13, color: 'var(--ink-soft)', marginTop: 4 }}>We've already imported your bar listing. Sign up with the email on file and the profile flips to your control.</div>
                                    </div>
                                </li>
                                <li style={{ display: 'grid', gridTemplateColumns: '32px 1fr', gap: 14, alignItems: 'flex-start' }}>
                                    <div className="pip-mono" style={{ fontSize: 13, color: 'var(--ember)', fontWeight: 600 }}>02</div>
                                    <div>
                                        <div style={{ fontFamily: 'var(--font-serif)', fontSize: 17, fontWeight: 500 }}>Edit your bio + contact</div>
                                        <div style={{ fontSize: 13, color: 'var(--ink-soft)', marginTop: 4 }}>Practice areas, direct line, languages, response window — all in one place.</div>
                                    </div>
                                </li>
                                <li style={{ display: 'grid', gridTemplateColumns: '32px 1fr', gap: 14, alignItems: 'flex-start' }}>
                                    <div className="pip-mono" style={{ fontSize: 13, color: 'var(--ember)', fontWeight: 600 }}>03</div>
                                    <div>
                                        <div style={{ fontFamily: 'var(--font-serif)', fontSize: 17, fontWeight: 500 }}>Receive matched intakes</div>
                                        <div style={{ fontSize: 13, color: 'var(--ink-soft)', marginTop: 4 }}>Geo + practice-area matched. Sent direct to your email — no broker, no upsell.</div>
                                    </div>
                                </li>
                            </ol>
                        </aside>
                    </div>
                </section>

                {/* FAQ */}
                {faqs.length > 0 && (
                    <section data-fa-section="faq" style={{ maxWidth: 1200, margin: '0 auto', padding: '48px 56px 80px' }}>
                        <h2 style={{ fontSize: 36, lineHeight: 1.1, letterSpacing: '-0.015em', marginBottom: 24 }}>Frequently asked</h2>
                        <div data-fa-faq-list style={{ display: 'flex', flexDirection: 'column' }}>
                            {faqs.map((f, i) => (
                                <details key={i} open={i === 0} style={{ borderBottom: '1px solid var(--rule)', padding: '20px 0' }}>
                                    <summary style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 24, cursor: 'pointer', listStyle: 'none' }}>
                                        <div style={{ fontFamily: 'var(--font-serif)', fontSize: 19, fontWeight: 500, color: 'var(--ink)', lineHeight: 1.3 }}>{f.question}</div>
                                        <span style={{ fontSize: 18, color: 'var(--ink-soft)', lineHeight: 1, marginTop: 2 }}>+</span>
                                    </summary>
                                    <p style={{ fontSize: 15, color: 'var(--ink-soft)', lineHeight: 1.65, marginTop: 12, maxWidth: 780 }}>{f.answer}</p>
                                </details>
                            ))}
                        </div>
                    </section>
                )}

                <DesignFooter />
            </div>
        </>
    );
}
